import { TheWestSection } from '@news-mono/common'
import { metrics, ResponsivePictureLayout } from '@news-mono/component-library'
import {
    AdTargeting,
    GetRouteAdTargeting,
    getSocialImageMeta,
    PageType,
    Topic,
} from '@news-mono/web-common'
import { layout } from '../../../App.routing'
import { billboardLeaderboardMrec } from '../../ad-units'
import { getTopicSponsor } from '../get-topic-sponsor'

const stmBannerDesktopX2 = require<string>('./assets/stm/stm-21st-desktop-2440x300-@2x.webp')
const stmBannerTabletX2 = require<string>('./assets/stm/stm-21st-tablet-1476x114-@2x.webp')
const stmBannerMobileX2 = require<string>('./assets/stm/stm-21st-mobile-822x186-@2x.webp')

const stmBannerDesktop = require<string>('./assets/stm/stm-21st-desktop-2440x300-@1x.webp')
const stmBannerTablet = require<string>('./assets/stm/stm-21st-tablet-1476x114-@1x.webp')
const stmBannerMobile = require<string>('./assets/stm/stm-21st-mobile-822x186-@1x.webp')

export const getSTMRouteInfo = (
    topic: Topic,
    getAdTargeting: GetRouteAdTargeting,
): PageType<TheWestSection> => {
    const { adUnitPath, ssAdUnits, topics } = getAdTargeting(
        'home',
        'default',
        topic,
    )

    const adTargeting: AdTargeting = {
        pageId: topic.title,
        adUnitPath,
        ssAdUnits,
        topics,
    }

    const socialImageUrl = topic?.metadata?.socialImageUrl
    const socialImageHeight = topic?.metadata?.socialImageHeight
    const socialImageWidth = topic?.metadata?.socialImageWidth

    // Curation to display in top 6 cards
    const curationName = 'stm'

    return {
        kind: 'page',
        heading: 'STM',
        pageType: 'topic',
        adTargeting,
        hideHeading: true,
        includeFromSectionInMetaDescription: !topic.seoDescription,
        pageMeta: {
            description:
                topic.seoDescription || `The Latest in ${topic.seoTitle}`,
            title: topic.seoTitle || topic.title,
            meta: getSocialImageMeta(
                socialImageUrl ||
                    '/static/share-images/share-400x400.png?imwidth=1024',
                socialImageHeight || '400',
                socialImageWidth || '400',
            ),
        },
        socialMeta: {
            title: topic.seoTitle || topic.title,
            description:
                topic.seoDescription || `The Latest in ${topic.seoTitle}`,
        },
        section: 'default',
        compositions: [
            // Default Compositions - Breaking News and Breadcrumb title
            layout.composition({
                type: 'box',
                props: {
                    containerWidth:
                        metrics.thewest.siteMetrics.mainContentWidth,
                    verticalGutters: ['outerMargin', 'unset'],
                },
                contentAreas: {
                    main: [
                        // Breaking News
                        layout.component({
                            type: 'breaking-news',
                            props: {
                                dataDefinitionArgs: {
                                    type: 'curation',
                                    name: 'breaking-news',
                                    offset: 0,
                                    pageSize: 1,
                                },
                            },
                        }),
                        // Breaking News Promo Strap
                        layout.component({
                            type: 'breaking-news-promo-strap',
                            props: {
                                dataDefinitionArgs: {
                                    type: 'curation',
                                    name: 'breaking-news-promo-strap',
                                    offset: 0,
                                    pageSize: 1,
                                },
                            },
                        }),
                        // Breadcrumb title
                        layout.component({
                            type: 'breadcrumb',
                            props: {
                                topic,
                                sponsor: getTopicSponsor(topic),
                                enableHeaderTag: true,
                                horizontalGutters: 'outerMargin',
                            },
                        }),
                    ],
                },
            }),
            // Main body composition
            layout.composition({
                type: 'thor',
                props: {
                    containerWidth:
                        metrics.thewest.siteMetrics.mainContentWidth,
                },
                contentAreas: {
                    main: [
                        // Image Banner
                        layout.component({
                            type: 'image-banner',
                            props: {
                                defaultSrc: stmBannerDesktopX2,
                                verticalGutters: ['unset', 'md'],
                                altText: 'STM',
                                sources: [
                                    {
                                        breakpoint: 'md',
                                        src: [
                                            {
                                                src: stmBannerDesktopX2,
                                                density: '2x',
                                            },
                                            {
                                                src: stmBannerDesktop,
                                                density: '1x',
                                            },
                                        ],
                                    },
                                    {
                                        breakpoint: 'xs',
                                        src: [
                                            {
                                                src: stmBannerTabletX2,
                                                density: '2x',
                                            },
                                            {
                                                src: stmBannerTablet,
                                                density: '1x',
                                            },
                                        ],
                                    },
                                    {
                                        src: [
                                            {
                                                src: stmBannerMobileX2,
                                                density: '2x',
                                            },
                                            {
                                                src: stmBannerMobile,
                                                density: '1x',
                                            },
                                        ],
                                    },
                                ],
                            },
                        }),
                        // Top 2 cards from curation
                        layout.component({
                            type: 'sierra',
                            props: {
                                verticalSpacing: 'gridGap',
                                fixedRatios: ['16:9'],
                                heroImageLayout:
                                    ResponsivePictureLayout.ObjectFitContain,
                                cardOrientation: {
                                    type: 'Landscape-Portrait',
                                    maxBreakpoint: 'md',
                                },
                                cardLayout: [
                                    'hero',
                                    'teaser-xsVisible-mdHidden-Image',
                                ],

                                dataDefinitionArgs: {
                                    type: 'curation',
                                    name: curationName || topic.id,
                                    offset: 0,
                                    pageSize: 2,
                                },
                            },
                        }),
                        // 4 Cards below from curation
                        layout.component({
                            type: 'lima',
                            props: {
                                verticalSpacing: 'xl',
                                fontScale: 1.2,
                                fixedRatios: ['16:9'],
                                hasBackground: true,
                                initialColumns: 1,
                                intermediateColumns: 2,
                                finalColumns: 4,
                                teaserMode: 'visible',
                                cardOrientation: {
                                    type: 'Landscape-Portrait',
                                },

                                dataDefinitionArgs: {
                                    type: 'curation',
                                    name: curationName || topic.id,
                                    offset: 2,
                                    pageSize: 4,
                                },
                            },
                        }),
                        // First ad leaderboard / billboard
                        ...billboardLeaderboardMrec('one'),
                        // Latest from Topic Listing
                        layout.component({
                            type: 'lima',
                            props: {
                                sectionHeader: {
                                    heading: `Latest from ${topic.title}`,
                                },
                                verticalSpacing: 'xl',
                                fontScale: 1.2,
                                fixedRatios: ['16:9'],
                                initialColumns: 1,
                                intermediateColumns: 2,
                                finalColumns: 4,
                                hasBackground: true,
                                cardOrientation: {
                                    type: 'Landscape-Portrait',
                                },
                                loadMoreEnabled: true,
                                dataDefinitionArgs: {
                                    type: 'listing',
                                    topics: [topic.id],
                                    includeSubTopics: false,
                                    paging: {
                                        page: 1,
                                        pageSize: 8,
                                    },
                                },
                            },
                        }),
                        // Bottom ad leaderboard / billboard
                        ...billboardLeaderboardMrec('two'),
                    ],
                },
            }),
        ],
    }
}
